import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { loadGoogleMaps, MAPS_ENABLED } from "../lib/maps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live map on the public tracking page: pickup + delivery pins, live driver
// marker(s) with breadcrumb trail, and a route line. Polls /live every 15s.
export function LiveTrackMap({ token }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const objs = useRef({ markers: [], route: null, trail: null });
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    if (!MAPS_ENABLED) { setUnavailable(true); return; }
    let active = true;
    loadGoogleMaps().then((gmaps) => {
      if (!active || !divRef.current) return;
      mapRef.current = new gmaps.Map(divRef.current, {
        center: { lat: 22.9734, lng: 78.6569 }, zoom: 5,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "cooperative",
      });
      setReady(true);
    }).catch(() => { if (active) setUnavailable(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!ready) return;
    let active = true;
    const gmaps = window.google.maps;

    const dot = (pos, title, color, scale) => new gmaps.Marker({
      position: pos, map: mapRef.current, title,
      icon: { path: gmaps.SymbolPath.CIRCLE, scale: scale || 7, fillColor: color,
              fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
    });

    const draw = (d) => {
      const map = mapRef.current;
      const o = objs.current;
      o.markers.forEach((m) => m.setMap(null)); o.markers = [];
      if (o.route) { o.route.setMap(null); o.route = null; }
      if (o.trail) { o.trail.setMap(null); o.trail = null; }

      const pts = [];
      if (d.pickup && typeof d.pickup.lat === "number") {
        const p = { lat: d.pickup.lat, lng: d.pickup.lng };
        o.markers.push(dot(p, `Pickup: ${d.pickup.label || ""}`, "#2F6F4E")); pts.push(p);
      }
      let driver = null;
      (d.trackers || []).forEach((t) => {
        if (typeof t.lat === "number" && typeof t.lng === "number") {
          driver = { lat: t.lat, lng: t.lng };
          o.markers.push(dot(driver, t.label || "Driver", "#2B5FB3", 9));
          if ((t.trail || []).length > 1) {
            o.trail = new gmaps.Polyline({
              path: t.trail.filter((x) => typeof x.lat === "number").map((x) => ({ lat: x.lat, lng: x.lng })),
              map, strokeColor: "#2B5FB3", strokeOpacity: 0.6, strokeWeight: 3,
            });
          }
        }
      });
      if (d.delivery && typeof d.delivery.lat === "number") {
        const p = { lat: d.delivery.lat, lng: d.delivery.lng };
        o.markers.push(dot(p, `Delivery: ${d.delivery.label || ""}`, "#8A2526")); 
      }

      // route line pickup -> driver(if any) -> delivery
      const line = [];
      if (d.pickup && typeof d.pickup.lat === "number") line.push({ lat: d.pickup.lat, lng: d.pickup.lng });
      if (driver) line.push(driver);
      if (d.delivery && typeof d.delivery.lat === "number") line.push({ lat: d.delivery.lat, lng: d.delivery.lng });
      if (line.length >= 2) {
        o.route = new gmaps.Polyline({ path: line, map, strokeColor: "#C84B31", strokeOpacity: 0.55, strokeWeight: 2,
          icons: [{ icon: { path: gmaps.SymbolPath.FORWARD_OPEN_ARROW }, offset: "50%" }] });
      }

      const all = line.length ? line : (driver ? [driver] : []);
      setHasData(all.length > 0);
      if (all.length === 1) { map.setCenter(all[0]); map.setZoom(12); }
      else if (all.length > 1) {
        const b = new gmaps.LatLngBounds();
        all.forEach((p) => b.extend(p));
        map.fitBounds(b, 50);
      }
    };

    const fetchLive = () => axios.get(`${API}/p/track/${token}/live`)
      .then((r) => { if (active) draw(r.data); }).catch(() => {});
    fetchLive();
    const id = setInterval(fetchLive, 15000);
    return () => { active = false; clearInterval(id); };
  }, [ready, token]);

  if (unavailable) return null;
  return (
    <div className="bg-white border border-[#E5E3DB] p-3 mb-4" data-testid="track-map-card">
      <div className="text-xs uppercase tracking-wide text-[#68665E] mb-2 px-1">Live Map</div>
      <div ref={divRef} className="w-full h-72 bg-[#F0EDE5]" data-testid="track-map" />
      {!hasData && (
        <div className="text-[11px] text-[#A09E96] mt-2 px-1" data-testid="track-map-note">
          Locating pickup / delivery… a live vehicle marker appears here when the driver shares location.
        </div>
      )}
    </div>
  );
}
